import type { ResponseCountNewNotifications } from "bonusx-api-sadm-manager";
import { NotificationApi } from "bonusx-api-sadm-manager";
import type { ConfigInterface } from "swr";
import useSWR from "swr";
import { defaultApiConfig } from "../../constants/env";
import { useIsLogged } from "../../hooks/auth/use-is-logged/use-is-logged";
import { swrBaseConfig } from "../common/swrBaseConfig";
import axiosInstance from "../services/axios-config-singleton/axios-config-singleton.instance";
import { useAbility } from "./useAbilityRules";

const KEY = "notification-count-api";

const REFRESH_INTERVAL_IN_MS = 15_000;

const swrConfig: ConfigInterface = {
  ...swrBaseConfig,
  refreshInterval: REFRESH_INTERVAL_IN_MS,
};

type NotificationType = "admin" | "caf" | "citizen";

const notificationTypes: Record<NotificationType, NotificationType> = {
  admin: "admin",
  caf: "caf",
  citizen: "citizen",
} as const;

type UseNotificationApiProps = {
  type: NotificationType;
};

export function useNotificationApi(props?: UseNotificationApiProps) {
  const notificationApi = new NotificationApi(defaultApiConfig, undefined, axiosInstance);

  const isLogged = useIsLogged();
  const abilityRules = useAbility();
  const canUpdateAccount = abilityRules.can("update", "Account");
  const canUpdateServiceDocumentStatus = abilityRules.can("update", "ServiceDocumentStatus");

  const notificationType = getNotificationType(canUpdateServiceDocumentStatus, props?.type);

  const shouldSkip = !isLogged || !canUpdateAccount;

  const fetcher = async () => {
    const response = await notificationApi.countNewNotification(notificationType);
    return response.data;
  };

  const {
    data,
    error,
    isValidating: loading,
    mutate,
  } = useSWR<ResponseCountNewNotifications | undefined>(shouldSkip ? null : KEY, fetcher, swrConfig);

  return {
    data,
    loading,
    error,
    revalidate: mutate,
  };
}

function getNotificationType(canUpdateServiceDocumentStatus: boolean, type?: NotificationType) {
  return type ?? (canUpdateServiceDocumentStatus ? notificationTypes.caf : notificationTypes.admin);
}
